import { useState, useEffect, useMemo } from 'react';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { Card, Typography } from '@mui/material';
import { Tag } from '../../stores/TagsStore';
import { useTaskStore, type Task } from '../../stores/TaskStore';
import { DraggableTaskList } from './DraggableTaskList';
import { TaskInput } from './TaskInput';
import { TagInputDialog } from './tagInput';
import { useTranslations } from 'next-intl';
import { TagWithDelete } from '../ui/Tag';

export type { Task };

export const TaskManager = () => {
  const t = useTranslations('TaskManager');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagInputOpen, setTagInputOpen] = useState(false);

  const [hydrated, setHydrated] = useState<boolean>(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const {
    tasks,
    addTask,
    toggleTask,
    deleteTask,
    getActiveTasks,
    initializeSampleData,
    toggleTaskPin,
    reorderTasks
  } = useTaskStore();

  useEffect(() => {
    initializeSampleData();
  }, [initializeSampleData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeTasks = useMemo(() => getActiveTasks(), [tasks]);

  return (
    <Card className="flex flex-col p-6 space-y-6 rounded-2xl shadow-2xs bg-task-manager" sx={{
      height: 'min(600px, 80vh)', // 使用较小值：600px或80vh
      transition: 'background-color 0.3s ease',
    }}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">{t('addNewTask')}</h3>
        </div>

        <div className="space-y-3">
          <TaskInput
            onAddTask={(description, selectedTags) => {
              addTask(description, selectedTags);
              setSelectedTags([]);
            }}
            onOpenTagDialog={() => setTagInputOpen(true)}
            selectedTags={selectedTags}
          />

          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 px-4 ">
              {selectedTags.map((tag) => (
                <TagWithDelete key={tag.id} label={tag.name} onDelete={() => {
                  setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
                }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {hydrated && activeTasks.length > 0 && (
        <div className="space-y-3 overflow-hidden flex-1 h-full">

          <DraggableTaskList
            tasks={activeTasks}
            onToggleTask={toggleTask}
            onDeleteTask={deleteTask}
            onTogglePin={toggleTaskPin}
            onReorderTasks={reorderTasks}
          />
        </div>
      )}

      {tasks.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <WatchLaterIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <Typography>{t('noTasks')}</Typography>
        </div>
      )}

      <TagInputDialog
        open={tagInputOpen}
        onClose={() => setTagInputOpen(false)}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
      />
    </Card>
  );
};